import { defineStore } from 'pinia'
import { ref, type Ref } from 'vue'
import { useSelectedPlanStore, type IPlanStore } from '@/stores/plans/selectedPlan.ts'
import { getUsersAccess } from '../../services/api/users/users.js'

type TType = {
	type: string
}

type finCareCostsOptionsObject = {
	title: string
	description: string
	link: string
	type: string
	buttonText: string
	subOptions: []
}

export type accessibleMembersObject = {
	memberId: string
	eid: string | null
	mdmSrcCode: string | null
}

type programObject = {
	category?: string
	cardLabel?: string
	cardDescription?: string
	programId?: string
	link?: null | string
}

export type rightsObject = {
	claims?: boolean
	healthReimbursementAccount?: boolean
	deductible?: boolean
	inpatientMedicalAdmissionRequest?: boolean
	personalHealthRecord?: boolean
	payBill?: boolean
	idCards?: boolean
	findCareAndCost?: boolean
	findCareAndCostOptions?: Array<finCareCostsOptionsObject>
	findCareAndCostDisplayText?: string
	findCareAndCostLink?: string
	findCareAndCostType?: string
	forms?: boolean
	rxSavingsSolutions?: boolean
	pharmacyCenter?: boolean
	whosCovered?: boolean
	choosePrimaryCareProvider?: boolean
	updatePolicy?: boolean
	updatePolicyMetallic?: boolean
	healthyLiving?: boolean
	notifications?: boolean
	coveragePolicy?: boolean
	coverageWhenTravelingAbroad?: boolean
	pharmacyLink?: null | string
	pharmacyType?: null | string
	pharmacyButtonText?: null | string
	accessibleMembers: Array<accessibleMembersObject>
	maPlanBenefits?: boolean
	maPlanBenefitsLink?: null | string
	updateOtherCoverageInformation?: null | string
	chatType?: string
	chatQueue?: string
	pageIdentifiers?: Array<TType>
	programs?: Array<programObject>
	fourWeeksEobProcessing?: boolean
	validationErrors?: []
}

export const useAccessRightsStore = defineStore(
	'accessRights',
	() => {
		// ====---------------====
		// 🏪 Stores
		// ====---------------====
		const planStore = useSelectedPlanStore().getStore()

		// ====---------------====
		// 🗄️ State
		// ====---------------====
		const state = ref<rightsObject | null>(null)
		const loaded = ref<boolean>(false)

		// ====---------------====
		// 🏃‍♂️ Getters
		// ====---------------====
		async function loadStore(plan: IPlanStore | null = null): Promise<rightsObject | null> {
			if (!loaded.value) await loadAccessRightsStore(plan)

			return getStore()
		}

		function getStore(): rightsObject | null {
			return state.value
		}

		function getAccessibleIdsArray(): Array<string> {
			const memberList = state.value?.accessibleMembers.map((x) => {
				return x.memberId
			})
			return memberList ?? []
		}

		function getAccessibleIdsString() {
			return getAccessibleIdsArray()?.join() ?? null
		}

		function getLoaded(): Ref<boolean> {
			return loaded
		}

		// ====---------------====
		// 🎬 Actions
		// ====---------------====
		function $reset() {
			state.value = null
			loaded.value = false
		}

		let loadAccessRightsStoreRequest: Promise<string> | null = null
		const loadAccessRightsStore = async (plan: IPlanStore | null = null) => {
			if (loadAccessRightsStoreRequest) {
				return loadAccessRightsStoreRequest
			}
			loadAccessRightsStoreRequest = new Promise(async (resolve, reject) => {
				const planData = plan ? plan : planStore
				if (!planData) return false
				try {
					await getUsersAccess(
						planData.username,
						planData.companyId,
						planData.memberId,
						planData.contractType
					)
						.then((res: rightsObject) => {
							loaded.value = true
							state.value = res
						})
						.catch((err: string) => {
							console.error(err)
						})

					resolve('')
				} catch (err) {
					reject(err)
				} finally {
					loadAccessRightsStoreRequest = null
				}
			})
			return loadAccessRightsStoreRequest
		}

		// ====---------------====
		// 🚀 Exports
		// ====---------------====
		return {
			state,
			loaded,
			getLoaded,
			loadStore,
			getStore,
			getAccessibleIdsArray,
			getAccessibleIdsString,
			$reset
		}
	},
	{
		persist: {
			storage: sessionStorage
		}
	}
)
